import React, { useEffect, useRef, useState, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  AppBar,
  Toolbar,
  Box,
  Typography,
  Avatar,
  IconButton,
  Tooltip,
  Button,
  Stack,
} from '@mui/material';
import AdminPanelSettingsRoundedIcon from '@mui/icons-material/AdminPanelSettingsRounded';
import LogoutRoundedIcon from '@mui/icons-material/LogoutRounded';
import BusinessRoundedIcon from '@mui/icons-material/BusinessRounded';
import PersonRoundedIcon from '@mui/icons-material/PersonRounded';
import MedicalServicesRoundedIcon from '@mui/icons-material/MedicalServicesRounded';
import GavelRoundedIcon from '@mui/icons-material/GavelRounded';
import LocalOfferRoundedIcon from '@mui/icons-material/LocalOfferRounded';
import CardGiftcardRoundedIcon from '@mui/icons-material/CardGiftcardRounded';
import ReceiptLongRoundedIcon from '@mui/icons-material/ReceiptLongRounded';
import AssessmentRoundedIcon from '@mui/icons-material/AssessmentRounded';
import MessageRoundedIcon from '@mui/icons-material/MessageRounded';
import FitnessCenterRoundedIcon from '@mui/icons-material/FitnessCenterRounded';
import CalendarMonthRoundedIcon from '@mui/icons-material/CalendarMonthRounded';
import SupportAgentRoundedIcon from '@mui/icons-material/SupportAgentRounded';
import ShieldRoundedIcon from '@mui/icons-material/ShieldRounded';
import SettingsRoundedIcon from '@mui/icons-material/SettingsRounded';
import FeedbackRoundedIcon from '@mui/icons-material/FeedbackRounded';
import MenuBookRoundedIcon from '@mui/icons-material/MenuBookRounded';
import FemaleRoundedIcon from '@mui/icons-material/FemaleRounded';
import VolunteerActivismRoundedIcon from '@mui/icons-material/VolunteerActivismRounded';
import BadgeRoundedIcon from '@mui/icons-material/BadgeRounded';
import HandshakeRoundedIcon from '@mui/icons-material/HandshakeRounded';
import ChevronLeftRoundedIcon from '@mui/icons-material/ChevronLeftRounded';
import ChevronRightRoundedIcon from '@mui/icons-material/ChevronRightRounded';
import { useAuth } from '../../context/AuthContext';
import axiosInstance from '../../api/axiosInstance';
import { API_ENDPOINTS } from '../../api/config';

const NAV_ITEMS = [
  { label: 'Corporates', path: '/admin/corporates', icon: <BusinessRoundedIcon sx={{ fontSize: 18 }} />, badgeKey: 'corporates' },
  { label: 'Individuals', path: '/admin/individuals', icon: <PersonRoundedIcon sx={{ fontSize: 18 }} /> },
  { label: 'Individual Clients', path: '/admin/clients', icon: <BadgeRoundedIcon sx={{ fontSize: 18 }} /> },
  { label: 'Channel Partners', path: '/admin/channel-partners', icon: <HandshakeRoundedIcon sx={{ fontSize: 18 }} /> },
  { label: 'Offers', path: '/admin/offers', icon: <LocalOfferRoundedIcon sx={{ fontSize: 18 }} /> },
  { label: 'Packages', path: '/admin/packages', icon: <CardGiftcardRoundedIcon sx={{ fontSize: 18 }} /> },
  { label: 'Billing', path: '/admin/billing', icon: <ReceiptLongRoundedIcon sx={{ fontSize: 18 }} /> },
  { label: 'Compliance', path: '/admin/compliance', icon: <GavelRoundedIcon sx={{ fontSize: 18 }} /> },
  { label: 'AI Usage', path: '/admin/ai-usage', icon: <AssessmentRoundedIcon sx={{ fontSize: 18 }} /> },
  { label: 'Messaging', path: '/admin/messaging', icon: <MessageRoundedIcon sx={{ fontSize: 18 }} /> },
  { label: 'GYM Services', path: '/admin/gym', icon: <FitnessCenterRoundedIcon sx={{ fontSize: 18 }} /> },
  { label: 'Tele Consultation', path: '/admin/teleconsultation', icon: <MedicalServicesRoundedIcon sx={{ fontSize: 18 }} /> },
  { label: 'Wellness Calendar', path: '/admin/wellness-calendar', icon: <CalendarMonthRoundedIcon sx={{ fontSize: 18 }} />, badgeKey: 'wellness_calendar' },
  { label: "Women's Health", path: '/admin/womens-health-content', icon: <FemaleRoundedIcon sx={{ fontSize: 18 }} /> },
  { label: 'Elderly Care', path: '/admin/elderly-care', icon: <VolunteerActivismRoundedIcon sx={{ fontSize: 18 }} />, badgeKey: 'elderly_enquiries' },
  { label: 'CS Team', path: '/admin/cs-team', icon: <SupportAgentRoundedIcon sx={{ fontSize: 18 }} /> },
  { label: 'Roles', path: '/admin/roles', icon: <ShieldRoundedIcon sx={{ fontSize: 18 }} /> },
  { label: 'Feedback', path: '/admin/feedback', icon: <FeedbackRoundedIcon sx={{ fontSize: 18 }} /> },
  { label: 'Settings', path: '/admin/settings', icon: <SettingsRoundedIcon sx={{ fontSize: 18 }} /> },
];

interface AdminHeaderProps {
  subtitle?: string;
}

const AdminHeader: React.FC<AdminHeaderProps> = ({ subtitle = 'Dashboard' }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuth();
  const [badgeCounts, setBadgeCounts] = useState<Record<string, number>>({});
  const navScrollRef = useRef<HTMLDivElement | null>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  const updateScrollState = useCallback(() => {
    const el = navScrollRef.current;
    if (!el) return;
    setCanScrollLeft(el.scrollLeft > 2);
    setCanScrollRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 2);
  }, []);

  useEffect(() => {
    updateScrollState();
    const el = navScrollRef.current;
    if (!el) return;
    el.addEventListener('scroll', updateScrollState, { passive: true });
    window.addEventListener('resize', updateScrollState);
    return () => {
      el.removeEventListener('scroll', updateScrollState);
      window.removeEventListener('resize', updateScrollState);
    };
  }, [updateScrollState]);

  const scrollBy = (delta: number) => {
    navScrollRef.current?.scrollBy({ left: delta, behavior: 'smooth' });
  };

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const isActive = (path: string) => location.pathname.startsWith(path);

  // Fetch badge counts on mount and every 60 seconds
  useEffect(() => {
    const fetchBadges = async () => {
      try {
        const response = await axiosInstance.get(API_ENDPOINTS.admin.badgeCounts);
        setBadgeCounts(response.data);
      } catch {
        // Silently ignore — badges are non-critical
      }
    };

    fetchBadges();
    const interval = setInterval(fetchBadges, 60000);
    return () => clearInterval(interval);
  }, []);

  // Keep the active tab visible when the route changes.
  useEffect(() => {
    const el = navScrollRef.current;
    const active = el?.querySelector<HTMLElement>('[data-active]');
    if (!el || !active) return;
    const cRect = el.getBoundingClientRect();
    const tRect = active.getBoundingClientRect();
    const left = el.scrollLeft + (tRect.left - cRect.left) - (cRect.width - tRect.width) / 2;
    el.scrollTo({ left: Math.max(0, left), behavior: 'smooth' });
  }, [location.pathname]);

  return (
    <AppBar
      position="sticky"
      elevation={0}
      sx={{
        background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 100%)',
        borderBottom: '1px solid rgba(255,255,255,0.06)',
      }}
    >
      <Toolbar sx={{ gap: 2, overflow: 'hidden' }}>
        <img
          src="/images/logo.svg"
          alt="Infinicare"
          style={{ height: 28, filter: 'brightness(0) invert(1)', cursor: 'pointer', flexShrink: 0 }}
          onClick={() => navigate('/admin/corporates')}
        />
        <Box sx={{ width: '1px', height: 28, background: 'rgba(255,255,255,0.15)', borderRadius: 1, flexShrink: 0 }} />
        <Avatar
          sx={{
            width: 40,
            height: 40,
            background: 'linear-gradient(135deg, #06b6d4, #8b5cf6)',
            borderRadius: '12px',
            fontWeight: 700,
            flexShrink: 0,
          }}
        >
          <AdminPanelSettingsRoundedIcon sx={{ fontSize: 22 }} />
        </Avatar>
        <Box sx={{ mr: 1, flexShrink: 0 }}>
          <Typography variant="h6" sx={{ fontWeight: 700, color: '#fff', lineHeight: 1.2 }}>
            Super Admin
          </Typography>
          <Typography variant="caption" sx={{ color: '#94a3b8' }}>
            {subtitle}
          </Typography>
        </Box>

        {/* Navigation */}
        <Box sx={{ flex: 1, minWidth: 0, position: 'relative', display: 'flex', alignItems: 'center' }}>
          {canScrollLeft && (
            <IconButton
              size="small"
              onClick={() => scrollBy(-240)}
              sx={{
                position: 'absolute',
                left: -4,
                zIndex: 2,
                color: '#fff',
                background: 'rgba(15,23,42,0.85)',
                backdropFilter: 'blur(4px)',
                boxShadow: '0 2px 8px rgba(0,0,0,0.3)',
                '&:hover': { background: 'rgba(30,41,59,0.95)' },
              }}
            >
              <ChevronLeftRoundedIcon fontSize="small" />
            </IconButton>
          )}
          {canScrollRight && (
            <IconButton
              size="small"
              onClick={() => scrollBy(240)}
              sx={{
                position: 'absolute',
                right: -4,
                zIndex: 2,
                color: '#fff',
                background: 'rgba(15,23,42,0.85)',
                backdropFilter: 'blur(4px)',
                boxShadow: '0 2px 8px rgba(0,0,0,0.3)',
                '&:hover': { background: 'rgba(30,41,59,0.95)' },
              }}
            >
              <ChevronRightRoundedIcon fontSize="small" />
            </IconButton>
          )}
          <Stack
            ref={navScrollRef}
            direction="row"
            spacing={0.5}
            sx={{
              flex: 1,
              minWidth: 0,
              flexWrap: 'nowrap',
              overflowX: 'auto',
              overflowY: 'hidden',
              scrollbarWidth: 'none',
              '&::-webkit-scrollbar': { display: 'none' },
            }}
          >
          {NAV_ITEMS.map((item) => {
            const count = item.badgeKey ? (badgeCounts[item.badgeKey] || 0) : 0;
            return (
              <Box key={item.path} data-active={isActive(item.path) || undefined} sx={{ position: 'relative', display: 'inline-flex', flexShrink: 0 }}>
                <Button
                  startIcon={item.icon}
                  onClick={() => navigate(item.path)}
                  sx={{
                    color: isActive(item.path) ? '#fff' : '#94a3b8',
                    background: isActive(item.path) ? 'rgba(255,255,255,0.1)' : 'transparent',
                    fontWeight: isActive(item.path) ? 700 : 500,
                    fontSize: '0.82rem',
                    textTransform: 'none',
                    borderRadius: '8px',
                    px: 1.5,
                    py: 0.6,
                    flexShrink: 0,
                    whiteSpace: 'nowrap',
                    '&:hover': {
                      background: 'rgba(255,255,255,0.08)',
                      color: '#fff',
                    },
                  }}
                >
                  {item.label}
                </Button>
                {count > 0 && (
                  <Box
                    sx={{
                      position: 'absolute',
                      top: 2,
                      right: 2,
                      minWidth: 18,
                      height: 18,
                      borderRadius: '999px',
                      background: '#ef4444',
                      color: '#fff',
                      fontSize: '0.65rem',
                      fontWeight: 700,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      px: '4px',
                      border: '2px solid #0f172a',
                      lineHeight: 1,
                      pointerEvents: 'none',
                    }}
                  >
                    {count > 99 ? '99+' : count}
                  </Box>
                )}
              </Box>
            );
          })}
          </Stack>
        </Box>

        <Stack direction="row" spacing={1} alignItems="center" sx={{ flexShrink: 0 }}>
          <Typography
            variant="body2"
            sx={{
              color: '#94a3b8',
              display: { xs: 'none', md: 'block' },
              whiteSpace: 'nowrap',
            }}
          >
            {user?.email}
          </Typography>
          <Tooltip title="Logout" arrow>
            <IconButton
              onClick={handleLogout}
              sx={{
                color: '#94a3b8',
                '&:hover': { color: '#f43f5e', background: 'rgba(244,63,94,0.1)' },
              }}
            >
              <LogoutRoundedIcon />
            </IconButton>
          </Tooltip>
        </Stack>
      </Toolbar>
    </AppBar>
  );
};

export default AdminHeader;
